import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useDemo } from '../../contexts/DemoContext';
import {
  createGoalDocument,
  getAvailableQuartersForCreation,
  getUserGoals,
  getCurrentQuarter
} from '../../services/goalsService';
import { getManagerForEmployee } from '../../services/userService';
import { createNewDemoGoal } from '../../services/demoData';
import { Goal, GoalType } from '../../types';
import { ArrowLeft, AlertCircle, Calendar, FileEdit, Send, FlaskConical } from 'lucide-react';

const defaultGoals: Goal[] = [
  { number: 1, title: '', description: '', type: 'normal', initialApproval: null, employeeRating: null, managerRating: null },
  { number: 2, title: '', description: '', type: 'normal', initialApproval: null, employeeRating: null, managerRating: null },
  { number: 3, title: '', description: '', type: 'normal', initialApproval: null, employeeRating: null, managerRating: null },
  { number: 4, title: '', description: '', type: 'stretch', initialApproval: null, employeeRating: null, managerRating: null },
  { number: 5, title: '', description: '', type: 'hard', initialApproval: null, employeeRating: null, managerRating: null },
];

const goalTypeLabels: Record<GoalType, string> = {
  normal: 'Normal Goal',
  stretch: 'Stretch Goal',
  hard: 'Hard Goal',
};

const goalTypeColors: Record<GoalType, string> = {
  normal: 'bg-blue-100 text-blue-700',
  stretch: 'bg-yellow-100 text-yellow-700',
  hard: 'bg-red-100 text-red-700',
};

const CreateGoals = () => {
  const { user, firebaseUser } = useAuth();
  const { isDemoMode, refreshDemo } = useDemo();
  const navigate = useNavigate();
  const [goals, setGoals] = useState<Goal[]>(defaultGoals);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableQuarters, setAvailableQuarters] = useState<{ quarter: number; year: number }[]>([]);
  const [selectedQuarter, setSelectedQuarter] = useState<{ quarter: number; year: number } | null>(null);

  useEffect(() => {
    const init = async () => {
      if (isDemoMode) {
        // In demo mode, allow creating for current quarter
        const { quarter, year } = getCurrentQuarter();
        setAvailableQuarters([{ quarter, year }]);
        setSelectedQuarter({ quarter, year });
        return;
      }

      // Get available quarters
      const quarters = getAvailableQuartersForCreation();

      // Check which quarters already have goals
      if (firebaseUser) {
        const userGoals = await getUserGoals(firebaseUser.uid);
        const existing = new Set(userGoals.map(g => `${g.quarter}-${g.year}`));

        // Filter out quarters that already have goals
        const available = quarters.filter(q => !existing.has(`${q.quarter}-${q.year}`));
        setAvailableQuarters(available);

        // Select the first available quarter
        if (available.length > 0) {
          setSelectedQuarter(available[0]);
        }
      } else {
        setAvailableQuarters(quarters);
        if (quarters.length > 0) {
          setSelectedQuarter(quarters[0]);
        }
      }
    };

    init();
  }, [firebaseUser, isDemoMode]);

  const updateGoal = (index: number, field: keyof Goal, value: string) => {
    const newGoals = [...goals];
    newGoals[index] = { ...newGoals[index], [field]: value };
    setGoals(newGoals);
  };

  const validateGoals = (): boolean => {
    for (let i = 0; i < goals.length; i++) {
      if (!goals[i].title.trim()) {
        setError(`Please enter a title for Goal ${i + 1}`);
        return false;
      }
      if (!goals[i].description.trim()) {
        setError(`Please enter a description for Goal ${i + 1}`);
        return false;
      }
    }
    return true;
  };

  const handleSaveDraft = async () => {
    setError(null);

    if (!selectedQuarter) {
      setError('Please select a quarter');
      return;
    }

    setSavingDraft(true);

    try {
      if (isDemoMode) {
        // Demo mode - save to in-memory state
        createNewDemoGoal(
          'demo.trainee@example.com',
          'Training User',
          'Training Employee',
          goals,
          selectedQuarter.quarter,
          selectedQuarter.year,
          'draft'
        );
        refreshDemo();
        navigate('/dashboard');
        return;
      }

      if (!firebaseUser || !user) {
        setError('You must be logged in to save goals');
        setSavingDraft(false);
        return;
      }

      const managerEmail = await getManagerForEmployee(user.email);

      if (!managerEmail) {
        setError('No manager assigned. Please contact your administrator.');
        setSavingDraft(false);
        return;
      }

      await createGoalDocument(
        firebaseUser.uid,
        user.email,
        user.displayName,
        user.title || 'Employee',
        goals,
        selectedQuarter.quarter,
        selectedQuarter.year,
        managerEmail,
        'draft'
      );

      navigate('/dashboard');
    } catch (err: any) {
      console.error('Error saving draft:', err);
      setError(err.message || 'Failed to save draft. Please try again.');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!selectedQuarter) {
      setError('Please select a quarter');
      return;
    }

    if (!validateGoals()) return;

    setSaving(true);

    try {
      if (isDemoMode) {
        // Demo mode - save to in-memory state with submitted status
        createNewDemoGoal(
          'demo.trainee@example.com',
          'Training User',
          'Training Employee',
          goals,
          selectedQuarter.quarter,
          selectedQuarter.year,
          'submitted'
        );
        refreshDemo();
        navigate('/dashboard');
        return;
      }

      if (!firebaseUser || !user) {
        setError('You must be logged in to submit goals');
        setSaving(false);
        return;
      }

      const managerEmail = await getManagerForEmployee(user.email);

      if (!managerEmail) {
        setError('No manager assigned. Please contact your administrator.');
        setSaving(false);
        return;
      }

      await createGoalDocument(
        firebaseUser.uid,
        user.email,
        user.displayName,
        user.title || 'Employee',
        goals,
        selectedQuarter.quarter,
        selectedQuarter.year,
        managerEmail,
        'submitted'
      );

      navigate('/dashboard');
    } catch (err: any) {
      console.error('Error creating goals:', err);
      setError(err.message || 'Failed to save goals. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (availableQuarters.length === 0 && !isDemoMode) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Create Goals</h1>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <Calendar className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-yellow-800 mb-2">No Quarters Available</h2>
          <p className="text-yellow-700">
            You've already created goals for all available quarters.
            Next quarter's goals can be created on the 1st of the last month of the current quarter.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Demo Mode Indicator */}
      {isDemoMode && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800">
          <FlaskConical className="w-5 h-5" />
          <span className="font-medium">Demo Mode</span>
          <span className="text-sm">- Goals created here are for training purposes only</span>
        </div>
      )}

      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Goals</h1>
          <p className="text-gray-500">Define your 5 quarterly goals</p>
        </div>
      </div>

      {/* Quarter Selection */}
      {availableQuarters.length > 1 && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Select Quarter
          </label>
          <div className="flex gap-3">
            {availableQuarters.map(q => (
              <button
                key={`${q.quarter}-${q.year}`}
                onClick={() => setSelectedQuarter(q)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedQuarter?.quarter === q.quarter && selectedQuarter?.year === q.year
                    ? 'bg-cps-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Q{q.quarter} {q.year}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedQuarter && (
        <div className="bg-cps-primary/10 rounded-lg px-4 py-2 mb-6 inline-flex items-center gap-2">
          <Calendar className="w-4 h-4 text-cps-dark" />
          <span className="text-cps-dark font-medium">
            Creating goals for Q{selectedQuarter.quarter} {selectedQuarter.year}
          </span>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {goals.map((goal, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xl font-bold text-gray-300">#{index + 1}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${goalTypeColors[goal.type]}`}>
                {goalTypeLabels[goal.type]}
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Goal Title
                </label>
                <input
                  type="text"
                  value={goal.title}
                  onChange={(e) => updateGoal(index, 'title', e.target.value)}
                  placeholder={`Enter title for ${goalTypeLabels[goal.type].toLowerCase()}`}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cps-primary focus:border-cps-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={goal.description}
                  onChange={(e) => updateGoal(index, 'description', e.target.value)}
                  placeholder="Clear outline of what would be required/steps to complete this goal"
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cps-primary focus:border-cps-primary resize-none"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-end gap-4">
        <button
          onClick={() => navigate('/dashboard')}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveDraft}
          disabled={savingDraft || saving || !selectedQuarter}
          className="inline-flex items-center gap-2 px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {savingDraft ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
              Saving...
            </>
          ) : (
            <>
              <FileEdit className="w-4 h-4" />
              Save Draft
            </>
          )}
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving || savingDraft || !selectedQuarter}
          className="inline-flex items-center gap-2 px-6 py-2 bg-cps-primary text-white rounded-lg hover:bg-cps-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Submit Goals
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CreateGoals;